/**
 * taxRegistrations.service — the org's tax registration numbers (CC-001 CH-2.3).
 *
 * This is the SSoT for GSTIN and friends, replacing the single
 * `org_billing_profiles.gstin` column: a company holds one registration PER
 * jurisdiction (India registers state-wise) and they expire — an LUT is renewed
 * every 15 March — so one field could never hold the truth.
 *
 * `is_active` is derived from the effectivity dates, never stored; a
 * registration is ended (POST /end) rather than deleted, so history survives.
 */
import { read, write } from './_base';
import {
  Billing_TaxRegistrations_Url,
  Billing_TaxRegistration_End_Url,
} from '../../api/api.endpoints';

export const REG_TYPES = ['GSTIN', 'TRN', 'ABN', 'LUT', 'VAT', 'OTHER'] as const;
export type RegType = (typeof REG_TYPES)[number];

export interface TaxRegistration {
  id: string;
  jurisdiction_code: string;
  reg_type: RegType;
  reg_value: string;
  effective_from: string;
  effective_to: string | null;
  expires_on: string | null;
  is_active: boolean;
}

export interface CreateTaxRegistrationInput {
  jurisdiction_code: string;
  reg_type: RegType;
  reg_value: string;
  effective_from?: string;
  /** Required by the API for LUT, which always carries a renewal date. */
  expires_on?: string;
}

export const listTaxRegistrations = () =>
  read<{ tax_registrations: TaxRegistration[] }>(
    Billing_TaxRegistrations_Url,
    'Failed to load tax registrations',
  ).then((r) => r?.tax_registrations ?? []);

export const createTaxRegistration = (body: CreateTaxRegistrationInput) =>
  write<TaxRegistration>('post', Billing_TaxRegistrations_Url, {
    body,
    errorTitle: 'Could not save the registration',
    successTitle: 'Registration saved',
  });

/** Ends an active registration (sets effective_to). History is kept. */
export const endTaxRegistration = (id: string) =>
  write<TaxRegistration>('post', Billing_TaxRegistration_End_Url(id), {
    errorTitle: 'Could not end the registration',
    successTitle: 'Registration ended',
  });
